import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import {
  fetchTransactionListPeriodReportAction,
  setError,
} from "../slices/transactionListReportSlice";
import { endOfMonth, format } from "date-fns";
import {
  SelectOption,
  buildMonthOptions,
  defaultMonthKey,
} from "../utils/availableMonths";

const parseKey = (key: string): { year: number; month: number } => {
  const [year, month] = key.split("-").map(Number);
  return { year, month };
};

export const useTransactionListReportBuilder = (earliestMonth: string) => {
  const dispatch = useDispatch();

  const report = useSelector(
    (state: RootState) => state.transactionListReport.report,
  );
  const loading = useSelector(
    (state: RootState) => state.transactionListReport.loading,
  );
  const error = useSelector(
    (state: RootState) => state.transactionListReport.error,
  );

  const [startKey, setStartKey] = useState<string>(() =>
    defaultMonthKey(earliestMonth, new Date()),
  );
  const [endKey, setEndKey] = useState<string>(() =>
    defaultMonthKey(earliestMonth, new Date()),
  );

  const monthOptions = useMemo<SelectOption[]>(
    () => buildMonthOptions(earliestMonth, new Date()),
    [earliestMonth],
  );

  const startMonthOptions = monthOptions;

  const endMonthOptions = useMemo<SelectOption[]>(
    () => monthOptions.filter((o) => o.value >= startKey),
    [monthOptions, startKey],
  );

  // Push end forward if it falls behind start
  useEffect(() => {
    if (endKey < startKey) {
      setEndKey(startKey);
    }
  }, [startKey, endKey]);

  const handleStartKeyChange = useCallback(
    (key: string) => {
      setStartKey(key);
      dispatch(setError(null));
    },
    [dispatch],
  );

  const handleEndKeyChange = useCallback(
    (key: string) => {
      setEndKey(key);
      dispatch(setError(null));
    },
    [dispatch],
  );

  const resolveBoundaries = useCallback(() => {
    const { year: sY, month: sM } = parseKey(startKey);
    const { year: eY, month: eM } = parseKey(endKey);
    const today = new Date();
    const isCurrentMonth =
      eY === today.getFullYear() && eM === today.getMonth() + 1;
    const start = new Date(sY, sM - 1, 1);
    const end = isCurrentMonth ? today : endOfMonth(new Date(eY, eM - 1, 1));
    return { start, end };
  }, [startKey, endKey]);

  const resolvedRange = useMemo(() => {
    const { start, end } = resolveBoundaries();
    return `${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`;
  }, [resolveBoundaries]);

  const handleGenerateReport = useCallback(() => {
    const { start, end } = resolveBoundaries();
    dispatch(
      fetchTransactionListPeriodReportAction({
        periodStart: format(start, "yyyy-MM-dd"),
        periodEnd: format(end, "yyyy-MM-dd"),
      }),
    );
  }, [dispatch, resolveBoundaries]);

  const canGenerate = startKey <= endKey;

  return {
    startKey,
    endKey,
    setStartKey: handleStartKeyChange,
    setEndKey: handleEndKeyChange,
    startMonthOptions,
    endMonthOptions,
    resolvedRange,
    report,
    loading,
    error,
    handleGenerateReport,
    canGenerate,
  };
};
